/**
 * THE one place that turns an instant into text. Every date the GUI shows goes
 * through here, so that one moment reads the same on every page and in the same
 * language: English, with the locale spelled out (a browser set to French must
 * not turn "5 minutes ago" into "il y a 5 minutes" next to English labels).
 *
 * Two kinds of display, chosen by the caller because only the caller knows what
 * the reader needs:
 *
 *   absolute  `2026-09-21 08:12:03`  what happened, precisely, to compare with a
 *             log (UTC unless asked otherwise: the logs an operator holds it up
 *             against are UTC). `formatDateTime`, `formatDate`.
 *   relative  `5 minutes ago`        how fresh it is, at a glance. `formatRelative`
 *             — drawn by `<RelativeTime>`, which also keeps the absolute
 *             time in a tooltip and re-reads the clock as it passes.
 *
 * A missing, zero or unparseable instant is an em dash, never "Invalid Date" or
 * "NaN": an API that leaves a timestamp out is a fact to show, not a crash.
 *
 * The rest of the app may not format a date by hand (`toLocale*`, `Intl.*`,
 * `toISOString`): `npm run lint:tokens` counts them outside this file.
 */

/** Epoch milliseconds, or an ISO-8601 string (what the GitHub Project returns). */
export type Instant = number | string | null | undefined;

const NO_DATE = "—";

/** An epoch in SECONDS (what AWS and Cognito timestamps are) as the milliseconds every formatter here takes; `null` when absent. */
export function fromEpochSeconds(seconds: number | null | undefined): number | null {
  return typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
}

// What a string instant may look like: an ISO-8601 date, or a date and time WITH its zone (`Z` or an
// offset: without one `Date` reads the reader's own clock and the same string means two moments). Anything else
// is not an instant — and must not be handed to `new Date`, whose legacy parser reads "0" as the year 2000
// and "12" as December: an API that sends a bare number as a string would print a plausible, wrong date.
const ISO_STRING = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2}))?$/;

/**
 * The instant as a `Date`, or `null` when there is none: absent, `0` (the epoch is "never" for an API that
 * leaves a timestamp at zero), a negative or non-finite number, an empty string, a string that is not
 * ISO-8601 (`"0"` included), or a value `Date` cannot represent.
 */
function toDate(value: Instant): Date | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return null;
  } else if (!ISO_STRING.test(value.trim())) {
    return null;
  }
  const date = new Date(typeof value === "string" ? value.trim() : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const pad = (n: number) => String(n).padStart(2, "0");

export type DatePrecision = "day" | "minute" | "second";

/**
 * `2026-09-21 08:12:03` (`precision: "second"`, the default), `2026-09-21 08:12`
 * (`"minute"`) or `2026-09-21` (`"day"`), in UTC. `zone: "local"` reads the
 * reader's own clock instead, for the rare date that is theirs (a device they
 * registered) rather than the system's.
 */
export function formatDateTime(
  value: Instant,
  { precision = "second", zone = "utc" }: { precision?: DatePrecision; zone?: "utc" | "local" } = {},
): string {
  const date = toDate(value);
  if (!date) return NO_DATE;
  const utc = zone === "utc";
  const day =
    `${utc ? date.getUTCFullYear() : date.getFullYear()}-` +
    `${pad((utc ? date.getUTCMonth() : date.getMonth()) + 1)}-` +
    `${pad(utc ? date.getUTCDate() : date.getDate())}`;
  if (precision === "day") return day;
  const hours = pad(utc ? date.getUTCHours() : date.getHours());
  const minutes = pad(utc ? date.getUTCMinutes() : date.getMinutes());
  if (precision === "minute") return `${day} ${hours}:${minutes}`;
  return `${day} ${hours}:${minutes}:${pad(utc ? date.getUTCSeconds() : date.getSeconds())}`;
}

/** `2026-09-21`. */
export function formatDate(value: Instant, opts: { zone?: "utc" | "local" } = {}): string {
  return formatDateTime(value, { ...opts, precision: "day" });
}

/** The instant as a machine-readable ISO string, for `<time dateTime>`; `undefined` when there is none. */
export function isoInstant(value: Instant): string | undefined {
  return toDate(value)?.toISOString();
}

// `numeric: "auto"` says "yesterday" and "now" where "1 day ago" and "0 seconds
// ago" would read as a machine talking; the locale is pinned (see above).
const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

const UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: "year", ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: "month", ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: "day", ms: 24 * 60 * 60 * 1000 },
  { unit: "hour", ms: 60 * 60 * 1000 },
  { unit: "minute", ms: 60 * 1000 },
  { unit: "second", ms: 1000 },
];

// Under this distance from now, an instant reads "just now" in either direction:
// a clock a few seconds off between this browser and the server must not print
// "in 3 seconds" next to something that has already happened.
const JUST_NOW_MS = 5_000;

/** `5 minutes ago`, `yesterday`, `in 3 hours` (a deadline), `just now`. */
export function formatRelative(value: Instant, now: number = Date.now()): string {
  const date = toDate(value);
  if (!date) return NO_DATE;
  const delta = date.getTime() - now;
  if (Math.abs(delta) < JUST_NOW_MS) return "just now";
  const abs = Math.abs(delta);
  for (const { unit, ms } of UNITS) {
    if (abs >= ms || unit === "second") return relative.format(Math.round(delta / ms), unit);
  }
  return "just now";
}
